import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'models';

const mapper = (state: RootState) => state.configurator.mapper;
const selectDocTypes = createSelector(mapper, ({ docTypes }) => docTypes);
const selectMapperFields = createSelector(mapper, ({ fields }) => fields);
const selectMapperEditRowId = createSelector(mapper, ({ templateEditRowID }) => templateEditRowID);
const selectMapperSelectedRowId = createSelector(mapper, ({ templateSelectedRowID }) => templateSelectedRowID);
const selectMapperRows = createSelector(mapper, ({ rows }) => rows);
const selectMapperIdDocType = createSelector(mapper, ({ activeDocTypeId }) => activeDocTypeId);
const selectMapperAreas = createSelector(mapper, ({ areas }) => areas);
const selectActiveOTemplateId = createSelector(mapper, ({ activeOTemplateId }) => activeOTemplateId);
const selectCurrentPdfPage = createSelector(mapper, ({ pdfCurrentPage }) => pdfCurrentPage);

const selectAllIdDocTypeNames = (id: string, name: string) =>
  createSelector(selectDocTypes, (docTypes) =>
    Object.values(docTypes).map((el) => {
      return {
        [id]: el?.idDocType,
        [name]: el?.docName,
      };
    }),
  );

const selectActiveDocType = createSelector(selectMapperIdDocType, selectDocTypes, (activeDocTypeId, docTypes) => {
  return docTypes.find((el) => el.idDocType === activeDocTypeId) || null;
});

const selectActiveOTemplate = createSelector(
  selectActiveOTemplateId,
  selectMapperIdDocType,
  selectDocTypes,
  (activeOTemplateId, activeDocTypeId, docTypes) => {
    if (activeDocTypeId && activeOTemplateId && docTypes) {
      const docType = docTypes.find((el) => el.idDocType === activeDocTypeId);
      if (docType) {
        return docType.oTemplateList.find((el) => el.idTemplate === activeOTemplateId);
      }
    }
    return null;
  },
);

const selectFieldNames = createSelector(selectMapperFields, (allfields) => {
  if (!allfields) return [];
  const { columnFields, fields } = allfields;
  const fieldNameTypes: {
    value: any;
    label: string;
    idField: number | null;
    idColumn: number | null;
  }[] = [];
  fields.forEach((el) => {
    fieldNameTypes.push({
      value: el.name,
      label: el.name,
      idField: el.idField,
      idColumn: null,
    });
  });
  columnFields.forEach((el) => {
    fieldNameTypes.push({ value: el.title, label: el.title, idColumn: el.idColumn, idField: null });
  });
  fieldNameTypes.sort((a, b) => {
    const nameA = a.label?.toLowerCase();
    const nameB = b.label?.toLowerCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
  return fieldNameTypes;
});

const selectIdFieldIdColumnFromRows = createSelector(selectMapperRows, selectMapperEditRowId, (rows, id) => {
  if (id === '') return { idField: null, idColumn: null };
  const idx = rows.findIndex((el) => Number(id) < Number(el.id));
  if (idx === -1) {
    // last row
    return {
      idField: (rows[rows.length - 1] as { idField: any })?.idField ?? null,
      idColumn: (rows[rows.length - 1] as { idColumn: any })?.idColumn ?? null,
    } as { idField: number | null; idColumn: number | null };
  }
  return {
    idField: (rows[idx - 1] as { idField: any })?.idField ?? null,
    idColumn: (rows[idx - 1] as { idColumn: any })?.idColumn ?? null,
  } as { idField: number | null; idColumn: number | null };
});

// it is used to select the single row or all subRow
const selectMapperSingleRowOrSubRow = createSelector(
  selectMapperRows,
  selectMapperSelectedRowId,
  selectMapperEditRowId,
  (rows, selectedRowId, editedRowID) => {
    if (selectedRowId === '' && editedRowID === '') return undefined;
    const selectedId = editedRowID !== '' ? editedRowID : selectedRowId;
    const idx = rows.findIndex((el) => Number(el.id) > Number(selectedId));
    if (idx === -1) {
      // then it is the last row or some subRow of the last row
      const row = rows[rows.length - 1];
      if (row?.id === selectedId) return row;
      const subRowIdx = row?.subRows?.findIndex((el: { id: string | undefined }) => el?.id === selectedId);
      if (subRowIdx >= 0) return row?.subRows[subRowIdx];
      return undefined;
    }
    if (rows[idx].id === selectedId) return rows[idx];
    const row = rows[idx - 1];
    if (row?.id === selectedId) return row;
    const subRowIdx = row?.subRows?.findIndex((el: { id: string | undefined }) => el?.id === selectedId);
    if (subRowIdx >= 0) return row?.subRows[subRowIdx];
    return undefined;
  },
);

const itHasSubRows = createSelector(selectMapperSingleRowOrSubRow, (row) => {
  if ((row as any)?.subRows?.length > 0) return true;
  return false;
});

export default {
  selectAllIdDocTypeNames,
  selectActiveDocType,
  selectActiveOTemplate,
  selectMapperIdDocType,
  selectDocTypes,
  selectActiveOTemplateId,
  selectMapperFields,
  selectMapperEditRowId,
  selectMapperSelectedRowId,
  selectFieldNames,
  selectMapperRows,
  selectMapperAreas,
  selectMapperSingleRowOrSubRow,
  selectCurrentPdfPage,
  selectIdFieldIdColumnFromRows,
  itHasSubRows,
};
